import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { BoolCell, DateCell, MoneyCell, MutedCell, NameCell, NumberCell, StatusChip } from '@/components/TableCells'
import { DeliveryStatusBadge } from '@/components/DeliveryStatusBadge'
import { InlineEditBar } from '@/components/InlineEditBar'
import { useInlineSave } from '@/hooks/useInlineSave'
import { isInlineMoney, type InlineEditSpec } from '@/lib/inlineEdit'
import { Order, OrdersApi, type OrderBulkInput } from '@/services/orders/ordersApi'
import { useLineItemFieldSettings } from '@/hooks/useLineItemFieldSettings'

// ─── Editable fields ───────────────────────────────────────────────────────

/**
 * What each editable column of the list may change, and the payload that
 * changes it. The budget is sent whole — the server replaces the periods it
 * receives — so its patch is rebuilt from the periods the order already has.
 */
function useOrderEditSpecs() {
  const { t } = useTranslation()

  return useMemo(() => {
    const text = (field: 'name' | 'externalReference'): InlineEditSpec<Order> => ({
      type: 'text',
      maxLength: 255,
      value: (order) => order[field] || null,
      patch: (value) => ({ [field]: value === null ? '' : String(value) }),
      ...(field === 'name'
        ? {
            validate: (value: unknown) =>
              String(value ?? '').trim() ? undefined : t('common.nameRequired'),
          }
        : {}),
    })

    const specs: Record<string, InlineEditSpec<Order>> = {
      name: text('name'),
      externalReference: text('externalReference'),
      objectState: {
        type: 'select',
        value: (order) => order.objectState || null,
        options: () => [
          { value: 'ACTIVE', label: t('common.active') },
          { value: 'INACTIVE', label: t('common.inactive') },
        ],
        patch: (value) => ({ objectState: String(value) }),
      },
      budget: {
        type: 'money',
        min: 0,
        // A budget is an amount within a period, and a list is no place to
        // invent the period an order has never had one for.
        readOnly: (order) => !order.budget?.length,
        currency: (order) => order.budget?.[0]?.amount?.currency ?? '',
        value: (order) => order.budget?.[0]?.amount ?? null,
        patch: (value, order) =>
          value === null || !isInlineMoney(value)
            ? {}
            : {
                budget: order.budget.map((period, index) =>
                  index === 0 ? { ...period, amount: value } : period,
                ),
              },
      },
      budgetRollover: {
        type: 'bool',
        value: (order) => order.budgetRollover,
        patch: (value) => ({ budgetRollover: value === true }),
      },
    }
    return specs
  }, [t])
}

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  advertiserName: 'advertiser.name',
  teamName: 'team.name',
  executionState: 'executionState',
  externalReference: 'externalReference',
  updateTime: 'updateTime',
  objectState: 'objectState',
  budgetRollover: 'budgetRollover',
  impressionDelivery: 'impressionDelivery',
  clickDelivery: 'clickDelivery',
  viewableDelivery: 'viewableDelivery',
  createTime: 'createTime',
}

interface OrderTableProps {
  orders: Order[]
  loading?: boolean
  emptyMessage?: string
  showAdvertiser?: boolean
  selection?: Order[]
  onSelectionChange?: (orders: Order[]) => void
  /** Offers the fields of the list for editing in place. On by default. */
  editable?: boolean
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function OrderTable({
  orders,
  loading,
  emptyMessage,
  showAdvertiser = true,
  selection,
  onSelectionChange,
  editable = true,
  sort,
  onSortChange,
}: OrderTableProps) {
  const { t } = useTranslation()
  const selectable = !!onSelectionChange
  const { orderObjectives: showBudget } = useLineItemFieldSettings(false)

  const specs = useOrderEditSpecs()
  const { edits, setEdit, bar } = useInlineSave<Order>({
    save: (rows) =>
      OrdersApi.updateMany(rows.map(({ id, patch }) => ({ id, ...patch }) as OrderBulkInput)),
    invalidateKeys: ['orders', 'advertiser-orders', 'order'],
    errorTitle: t('orders.saveFailed'),
  })

  const columns = useMemo<ColumnDef<Order, unknown>[]>(() => [
    ...(selectable ? [selectionColumn<Order>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200, edit: specs.name },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    ...(showAdvertiser ? [{
      accessorKey: 'advertiserName',
      header: t('common.advertiser'),
      meta: { minWidth: 140 },
      cell: ({ row }: { row: { original: Order } }) => <MutedCell value={row.original.advertiserName} />,
    }] : []),
    {
      accessorKey: 'teamName',
      header: t('common.team'),
      meta: { minWidth: 140 },
      cell: ({ row }) => <MutedCell value={row.original.teamName} />,
    },
    {
      accessorKey: 'executionState',
      header: t('common.delivery'),
      size: 160,
      cell: ({ row }) => <DeliveryStatusBadge status={row.original.executionState} />,
    },
    {
      accessorKey: 'externalReference',
      header: t('common.externalReference'),
      size: 140,
      meta: { edit: specs.externalReference },
      cell: ({ row }) => <MutedCell value={row.original.externalReference} />,
    },
    {
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 130,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    },
    optionalColumn<Order>({
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      meta: { edit: specs.objectState },
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    }),
    ...(showBudget ? [optionalColumn<Order>({
      id: 'budget',
      header: t('orders.budget'),
      accessorFn: (row) => row.budget?.[0]?.amount?.amount ?? 0,
      size: 130,
      meta: { edit: specs.budget },
      cell: ({ row }) => <MoneyCell value={row.original.budget?.[0]?.amount} />,
    }),
    optionalColumn<Order>({
      id: 'budgetPeriod',
      header: t('orders.budgetPeriod'),
      enableSorting: false,
      meta: { minWidth: 170 },
      cell: ({ row }) => {
        const period = row.original.budget?.[0]
        return <MutedCell value={period ? `${period.startDay} → ${period.endDay}` : null} />
      },
    }),
    optionalColumn<Order>({
      accessorKey: 'budgetRollover',
      header: t('orders.budgetRollover'),
      size: 130,
      meta: { edit: specs.budgetRollover },
      cell: ({ row }) => <BoolCell value={row.original.budgetRollover} />,
    })] : []),
    optionalColumn<Order>({
      accessorKey: 'impressionDelivery',
      header: t('orders.impressions'),
      size: 120,
      cell: ({ row }) => <NumberCell value={row.original.impressionDelivery ?? null} />,
    }),
    optionalColumn<Order>({
      accessorKey: 'clickDelivery',
      header: t('orders.clicks'),
      size: 100,
      cell: ({ row }) => <NumberCell value={row.original.clickDelivery ?? null} />,
    }),
    optionalColumn<Order>({
      accessorKey: 'viewableDelivery',
      header: t('orders.viewable'),
      size: 110,
      cell: ({ row }) => <NumberCell value={row.original.viewableDelivery ?? null} />,
    }),
    optionalColumn<Order>({
      id: 'spendDelivery',
      header: t('orders.spend'),
      accessorFn: (row) => row.spendDelivery?.amount ?? 0,
      size: 120,
      cell: ({ row }) => <MoneyCell value={row.original.spendDelivery} />,
    }),
    optionalColumn<Order>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ], [t, selectable, showAdvertiser, showBudget, specs])

  return (
    <>
      <DataTable
        columns={columns}
        data={orders}
        loading={loading}
        emptyMessage={emptyMessage ?? t('orders.noOrdersFound')}
        columnsKey="orders"
        rowHref={(row) => `/advertising/orders/${row.id}`}
        selection={selection}
        onSelectionChange={onSelectionChange}
        edits={editable ? edits : undefined}
        onEditChange={editable ? setEdit : undefined}
        serverSort={
          onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
        }
      />
      <InlineEditBar {...bar} />
    </>
  )
}
